import React, { useState, useEffect } from "react";
import { ArrowRight, Clock, Users, Target } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Book, Cpu, LineChart, Globe, Settings, Rocket } from "lucide-react";

const icons = [Book, Cpu, LineChart, Globe, Settings, Rocket];

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(progress * value));
      
      if (progress >= 1) {
        clearInterval(timer);
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{count}</span>;
};



const GlowingCard = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, scale: 0.95 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    whileHover={{ 
      scale: 1.02,
      boxShadow: "0 20px 40px rgba(6, 182, 212, 0.15)",
    }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className={`
      relative bg-gradient-to-br from-slate-900/90 via-slate-800/90 to-slate-900/90
      backdrop-blur-xl border border-slate-700/50 rounded-2xl
      shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500
      before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r 
      before:from-cyan-500/10 before:to-blue-500/10 before:opacity-0 
      hover:before:opacity-100 before:transition-opacity before:duration-500
      ${className}
    `}
  >
    <div className="relative z-10">
      {children}
    </div>
  </motion.div>
);

const CurriculumPage = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, -50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -25]);

  return (
    <div className="min-h-screen text-white relative">
      {/* Your background component will go here */}

      <div className="relative px-6 py-16 space-y-20">
        {/* Hero Section */}
        <motion.div 
          className="text-center space-y-8 max-w-6xl mx-auto"
          style={{ y: y1 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-cyan-400 font-orbitron mt-20 via-blue-400 to-purple-400 bg-clip-text text-transparent leading-tight">
              Your Path to{" "}
              <span className="relative">
                <span className="relative z-10">Algorithmic Trading</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-xl"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 0.8, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </span>{" "}
              Mastery
            </h1>
            
            <motion.p 
              className="text-xl md:text-2xl text-slate-300 font-orbitron  font-light max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Master AI-Powered Trading Strategies & Fund Management
            </motion.p>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {[
              { icon: Clock, label: "Weeks", value: 20, suffix: "" },
              { icon: Users, label: "Modules", value: 6, suffix: "" },
              { icon: Target, label: "Success Rate", value: 85, suffix: "%" },
            ].map((stat, idx) => (
              <GlowingCard key={idx} delay={idx * 0.1} className="p-6 text-center">
                <stat.icon className="h-12 w-12 text-cyan-400 mx-auto mb-4" />
                <div className="text-3xl font-bold text-white mb-2">
                  <AnimatedCounter value={stat.value} />
                  {stat.suffix}
                </div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </GlowingCard>
            ))}
          </motion.div>
        </motion.div>

        {/* Curriculum Section */}
        <motion.div 
          className="max-w-7xl mx-auto"
          style={{ y: y2 }}
        >
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4">
              Curriculum Breakdown
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 mx-auto rounded-full" />
          </motion.div>

          {sections.map((section, sIdx) => {
            const Icon = icons[sIdx % icons.length];
            return (
              <motion.div
                key={sIdx}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: sIdx * 0.1 }}
                viewport={{ once: true }}
                className="mb-20"
              >
                {/* Section Header */}
                <motion.div 
                  className="flex items-center gap-4 mb-12"
                  whileHover={{ x: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl blur-lg opacity-50" />
                    <div className="relative bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-xl">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {section.title}
                    </h3>
                    <div className="text-slate-400 text-sm">
                      Module {sIdx + 1} • {section.weeks.length} Weeks
                    </div>
                  </div>
                </motion.div>

                {/* Week Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.weeks.map((week, idx) => (
                    <GlowingCard key={idx} delay={idx * 0.1}>
                      <div className="p-8 space-y-6">
                        {/* Week Header */}
                        <div className="flex items-center justify-between">
                          <h4 className="text-xl font-bold text-white">
                            {week.label}
                          </h4>
                          <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {week.label.split(' ')[1]}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="relative">
                          <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-cyan-400 to-blue-400"
                              initial={{ width: 0 }}
                              whileInView={{ width: "100%" }}
                              transition={{ duration: 1, delay: idx * 0.1 }}
                              viewport={{ once: true }}
                            />
                          </div>
                        </div>

                        {/* Topics */}
                        <ul className="space-y-3">
                          {week.topics.map((topic, tIdx) => (
                            <motion.li
                              key={tIdx}
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: (idx * 0.1) + (tIdx * 0.05) }}
                              viewport={{ once: true }}
                              className="flex items-start gap-3 text-slate-300"
                            >
                              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                              <span className="text-sm leading-relaxed">{topic}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </GlowingCard>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Enhanced CTA Section */}
        <motion.div
          className="text-center space-y-8 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="relative">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 blur-3xl"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <div className="relative bg-gradient-to-r font-orbitron  from-slate-900/90 to-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-12">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-6">
                Ready to Become an AI-Powered Algo Trader?
              </h2>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Apply today to join KSQuantitative's Hybrid Algorithmic Trading Program and transform your financial future.
              </p>
              
               <motion.a
                href="/registration" // Replace with your actual application URL
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-10 py-4 rounded-2xl text-lg font-semibold overflow-hidden inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="relative z-10 flex items-center gap-3">
                  Apply Now
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const sections = [
  {
    title: "Foundations of Algorithmic Trading",
    weeks: [
      {
        label: "Week 1",
        topics: ["Intro to Algorithmic Trading & Markets", "Types of Strategies: Trend, Arbitrage", "Ethics & Regulation"]
      },
      {
        label: "Week 2",
        topics: ["Python Basics & Syntax", "Data Types, Functions, Loops", "Intro to Jupyter & VS Code"]
      }
    ]
  },
  {
    title: "Programming & Data Handling",
    weeks: [
      {
        label: "Week 3",
        topics: ["NumPy Essentials", "Pandas for Time Series", "Data Acquisition: APIs, CSVs"]
      },
      {
        label: "Week 4",
        topics: ["Strategy Design Patterns", "Implementing Indicators in Python", "Debugging Basics"]
      },
      {
        label: "Week 5",
        topics: ["MQL5 Fundamentals", "MT5 EA Development Basics", "MQL5 vs Python Comparison"]
      },
      {
        label: "Week 6",
        topics: ["Chart Patterns & Indicators", "Hands-on RSI, MACD, Bollinger Bands", "Simple Strategy Build"]
      }
    ]
  },
  {
    title: "Strategy Development & Machine Learning",
    weeks: [
      {
        label: "Week 7",
        topics: ["Intro to Quant Strategies", "Statistical Analysis with Pandas", "Mean Reversion, Momentum"]
      },
      {
        label: "Week 8",
        topics: ["Machine Learning Basics", "SVM, Decision Trees", "Train/Test Splits for Financial Data"]
      },
      {
        label: "Week 9",
        topics: ["Neural Networks in Trading", "Feature Engineering", "Sklearn Pipelines"]
      }
    ]
  },
  {
    title: "Market Applications",
    weeks: [
      {
        label: "Week 10",
        topics: ["Stock Market Strategies", "Pairs Trading, Event-Driven Models", "Index Arbitrage"]
      },
      {
        label: "Week 11",
        topics: ["Crypto Trading Tactics", "Market Making, DeFi Logic", "Scalping in Volatile Assets"]
      },
      {
        label: "Week 12",
        topics: ["Forex Strategy Development", "Carry Trade, News Events", "Risk/Reward Trade-Offs"]
      }
    ]
  },
  {
    title: "Backtesting & Deployment",
    weeks: [
      {
        label: "Week 13",
        topics: ["Backtesting Concepts", "Backtrader, Zipline Setup", "Performance Metrics"]
      },
      {
        label: "Week 14",
        topics: ["Walk Forward Analysis", "Parameter Optimization", "Genetic Algorithms"]
      },
      {
        label: "Week 15",
        topics: ["Risk Management Tools", "Position Sizing, SL/TP", "Diversification Models"]
      },
      {
        label: "Week 16",
        topics: ["MQL5 Expert Advisors", "Advanced MT5 Strategy Development", "Python-MQL5 Integration"]
      },
      {
        label: "Week 17",
        topics: ["API Integrations", "Deploying to MT5 & FXOpen", "Live Paper Trading Workflow"]
      }
    ]
  },
  {
    title: "Capstone & Live Trading Preparation",
    weeks: [
      {
        label: "Week 18",
        topics: ["Strategy Validation via Darwinex Zero", "Compliance & Regulations", "Drawdown Analysis"]
      },
      {
        label: "Week 19",
        topics: ["Team Projects: Strategy Builds", "Simulated Performance Reviews", "Mentor Check-Ins"]
      },
      {
        label: "Week 20",
        topics: ["Presentation & Reporting", "Capital Allocation Review", "Fund Management Setup & Scaling Plan"]
      }
    ]
  }
];

export default CurriculumPage;